import React, { useEffect, useRef, useState } from "react"
import * as config from "../../../config.js"
import { CustomFormFieldsFragment, CustomFormsConfigurationFieldsFragment } from "../../../graphql-types"
import * as styles from "./custom-form.module.scss"
import * as inputsStyles from "../layout/inputs.module.scss"
import { graphql } from "gatsby"
import Input, { ValidationType } from "../layout/form/input"
import Textarea from "../layout/form/textarea"
import RadioButton from "../layout/form/radio-button"
import Checkbox from "../layout/form/checkbox"
import Select from "../layout/form/select"
import RichDatoCmsContent from "../rich-datocms-content/rich-datocms-content"
import FileInput from "../layout/form/file-input"
import { useForm, FieldValues } from "react-hook-form"
import FormError, { GlobalFormError } from "../layout/form/form-error"
import MandatoryIndicator from "../layout/form/mandatory-indicator"
import { sendEmailV2, importFiles } from "../../api/notifications"
import { pushGtmEvent } from "../../gtm/interaction-event"
import { getFieldName } from "./field-name-checker"
import ReCAPTCHA from "react-google-recaptcha"

type RenderProps = {
  data: CustomFormFieldsFragment
  locale: string
  configuration: CustomFormsConfigurationFieldsFragment
  formCaptchaKey: string
  titleHeadingLevel?: 2 | 3
}

const CustomForm: React.FC<RenderProps> = ({ data, locale, configuration, formCaptchaKey, titleHeadingLevel = 2 }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm()
  const formId = data.id.replace("DatoCmsForm-", "")
  const [formValues, setFormValues] = useState<Map<string, string | boolean | FileList>>(new Map())
  const [formState, setFormState] = useState<FormState>(FormState.Pending)
  const [firstClick, setFirstClick] = useState(true)
  const [captchaValue, setCaptchaValue] = useState("")
  const captchaContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const getTextWidth = (text: string, inputElement: HTMLElement) => {
      const span = document.createElement("span")
      span.textContent = text
      span.style.visibility = "hidden"
      span.style.font = window.getComputedStyle(inputElement).font
      document.body.appendChild(span)
      const width = span.offsetWidth
      document.body.removeChild(span)
      return width
    }

    const textFields = document.querySelectorAll<HTMLElement>(`.${styles.textField}`)
    textFields.forEach(textField => {
      const placeholderWidth = getTextWidth(textField.getAttribute("placeholder") || "", textField)
      const inputWidth = textField.offsetWidth

      if (placeholderWidth + 20 > inputWidth) {
        textField.style.maxWidth = placeholderWidth + 20 + "px"
      }
    })
  }, [])

  useEffect(() => {
    const container = captchaContainerRef.current
    if (!container) {
      return
    }

    const stripPresentationalIframeAttributes = () => {
      container.querySelectorAll("iframe").forEach(iframe => {
        iframe.removeAttribute("frameborder")
        iframe.removeAttribute("marginheight")
        iframe.removeAttribute("marginwidth")
        iframe.removeAttribute("scrolling")
      })
    }

    stripPresentationalIframeAttributes()
    const observer = new MutationObserver(stripPresentationalIframeAttributes)
    observer.observe(container, { childList: true, subtree: true, attributes: true })
    return () => observer.disconnect()
  }, [formCaptchaKey])

  function onChange(fieldId, value) {
    const newValues = new Map(formValues)
    newValues.set(fieldId, value)
    setFormValues(newValues)
    if (errors[fieldId]) {
      clearErrors(fieldId)
    }
  }

  function pushGtmFirstClickEvent() {
    if (firstClick) {
      pushGtmEvent("Form", "Form", "Form 1st click", data.title)
      setFirstClick(false)
    }
  }

  function onSubmit(formData: FieldValues) {
    const submitForm = async () => {
      pushGtmEvent("eventga", "Form", "Form Submit", data.title)

      const notEmptyEmailFieldIds = data.fieldsList
        .filter(field => field.__typename === "DatoCmsUserEmailField")
        .map(field => field.id)
        .filter(fieldId => !!formData[fieldId])
      const notEmptyFiles = data.fieldsList
        .filter(field => field.__typename === "DatoCmsFileField")
        .map(field => field.id)
        .filter(fieldId => !!formData[fieldId] && !!formData[fieldId][0])
        .map(fieldId => formData[fieldId][0])

      setFormState(FormState.Submitting)

      const uploadedFiles = []
      const results = await Promise.allSettled(notEmptyFiles.map(importFiles))
      let importError = false
      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          uploadedFiles.push(result.value)
        } else {
          importError = true
          const fieldId = data.fieldsList.find(
            field => field.__typename === "DatoCmsFileField" && formData[field.id]?.[0] === notEmptyFiles[index]
          )?.id

          if (fieldId) {
            setError(fieldId, {
              type: "server",
              message: `Erreur à l'import du fichier, veuillez réessayer. Si l'erreur persiste, vérifier ou changer votre fichier : "${notEmptyFiles[index].name}"`,
            })
          }
        }
      })
      if (importError) {
        setFormState(FormState.Error)
        return
      }
      const prmColor = config.primary_color
      const payload = {
        trigram: process.env.GATSBY_SUBSIDIARY_TRIGRAM,
        idForm: formId,
        fields: data.fieldsList

          .filter(field => !["DatoCmsSimpleText", "DatoCmsFileField"].includes(field.__typename))
          .map(field => {
            if ("name" in field) {
              if (field.__typename === "DatoCmsCheckboxField") {
                return { key: field.name, value: formData[field.id] ? "✓" : "✖︎" }
              }
              return { key: field.name, value: formData[field.id] }
            }
          }),

        logo: configuration.logoSubsidiary?.url || "",
        // prmColor = {red: 0, green: 0, blue: 0, alpha: 0}, we need to convert it to hex
        primaryColor: `#${prmColor.red.toString(16).padStart(2, "0")}${prmColor.green.toString(16).padStart(2, "0")}${prmColor.blue.toString(16).padStart(2, "0")}`,

        locale: locale || "fr",
        files: uploadedFiles,
      }

      const subsidiaryEmailPromise = sendEmailV2({
        ...payload,
      })

      const userEmailPromises = notEmptyEmailFieldIds.map(emailFieldId => {
        const { fields, files, ...payloadWithoutFieldsAndFiles } = payload
        sendEmailV2({
          ...payloadWithoutFieldsAndFiles,
          email: formData[emailFieldId],
        })
      })

      return Promise.all([subsidiaryEmailPromise, ...userEmailPromises]).then(
        () => setFormState(FormState.Success),
        () => setFormState(FormState.Error)
      )
    }

    if (
      !formCaptchaKey ||
      (formCaptchaKey && captchaValue) ||
      (window.location.hostname === "localhost" && process.env.GATSBY_SUBSIDIARY_TRIGRAM.toUpperCase() === "KWP")
    ) {
      submitForm()
    } else {
      console.warn("Captcha not valid", process.env.GATSBY_SUBSIDIARY_TRIGRAM)
    }
  }

  return (
    <form className={styles.customForm} onSubmit={handleSubmit(onSubmit)} noValidate={true}>
      {titleHeadingLevel === 3 ? (
        <h3 className={styles.title}>{data.title}</h3>
      ) : (
        <h2 className={styles.title}>{data.title}</h2>
      )}
      {data.fieldsList.map(field => {
        switch (field.__typename) {
          case "DatoCmsSingleLineTextField": {
            const validationType = toValidationType(field.format)
            const formatHintId = `${field.id}-format-hint`
            const formatErrorId = `${field.id}-format-error`
            const inputType =
              validationType === ValidationType.Email
                ? "email"
                : validationType === ValidationType.PhoneNumber
                  ? "tel"
                  : "text"

            return (
              <div className={inputsStyles.formPart + " " + styles.formPart} key={field.id}>
                <label htmlFor={field.id} className={inputsStyles.label}>
                  {field.name}
                  {field.mandatory && <MandatoryIndicator label={configuration.requiredFieldError} />}
                </label>
                {validationType === ValidationType.Email && (
                  <span id={formatHintId} className={styles.formatHint}>
                    {configuration.emailFieldError}
                  </span>
                )}
                {validationType === ValidationType.PhoneNumber && (
                  <span id={formatHintId} className={styles.formatHint}>
                    {configuration.phoneNumberFieldError}
                  </span>
                )}
                <Input
                  id={field.id}
                  type={inputType}
                  name={field.id}
                  autocomplete={getFieldName(field.name)}
                  placeholder={field.placeholder}
                  required={field.mandatory}
                  className={styles.textField}
                  aria-required={field.mandatory}
                  {...(validationType === ValidationType.Email || validationType === ValidationType.PhoneNumber
                    ? {
                        describedBy:
                          errors[field.id]?.type === "pattern" ? `${formatHintId} ${formatErrorId}` : formatHintId,
                      }
                    : {})}
                  register={register}
                  validationType={validationType}
                  hasErrors={!!errors[field.id]}
                  onFocus={pushGtmFirstClickEvent}
                />
                <FormError errors={errors} name={field.id} type="required">
                  {configuration.requiredFieldError}
                </FormError>
                {validationType === ValidationType.PhoneNumber && (
                  <FormError errors={errors} name={field.id} type="pattern" id={formatErrorId}>
                    {configuration.phoneNumberFieldError}
                  </FormError>
                )}
                {validationType === ValidationType.Email && (
                  <FormError errors={errors} name={field.id} type="pattern" id={formatErrorId}>
                    {configuration.emailFieldError}
                  </FormError>
                )}
              </div>
            )
          }

          case "DatoCmsUserEmailField": {
            const emailHintId = `${field.id}-format-hint`
            const emailErrorId = `${field.id}-format-error`

            return (
              <div className={inputsStyles.formPart + " " + styles.formPart} key={field.id}>
                <label htmlFor={field.id} className={inputsStyles.label}>
                  {field.name}
                  {field.mandatory && <MandatoryIndicator label={configuration.requiredFieldError} />}
                </label>
                <span id={emailHintId} className={styles.formatHint}>
                  {configuration.emailFieldError}
                </span>
                <Input
                  id={field.id}
                  type="email"
                  name={field.id}
                  autocomplete={getFieldName(field.name)}
                  placeholder={field.placeholder}
                  required={field.mandatory}
                  aria-required={field.mandatory}
                  className={styles.textField}
                  register={register}
                  validationType={ValidationType.Email}
                  hasErrors={!!errors[field.id]}
                  describedBy={errors[field.id]?.type === "pattern" ? `${emailHintId} ${emailErrorId}` : emailHintId}
                  onFocus={pushGtmFirstClickEvent}
                />
                <FormError errors={errors} name={field.id} type="required">
                  {configuration.requiredFieldError}
                </FormError>
                <FormError errors={errors} name={field.id} type="pattern" id={emailErrorId}>
                  {configuration.emailFieldError}
                </FormError>
              </div>
            )
          }

          case "DatoCmsMultiLinesTextField":
            return (
              <div className={inputsStyles.formPart + " " + styles.formPart} key={field.id}>
                <label
                  htmlFor={field.id}
                  className={inputsStyles.label + (field.mandatory ? " " + inputsStyles.labelRequired : "")}
                >
                  {field.name}
                </label>
                <Textarea
                  id={field.id}
                  name={field.id}
                  placeholder={field.placeholder}
                  required={field.mandatory}
                  hasErrors={!!errors[field.id]}
                  register={register}
                  onFocus={pushGtmFirstClickEvent}
                />
                <FormError errors={errors} name={field.id} type="required">
                  {configuration.requiredFieldError}
                </FormError>
              </div>
            )

          case "DatoCmsRadioButtonsField":
            return (
              <fieldset className={inputsStyles.formPart + " " + styles.formPart} key={field.id}>
                <legend className={inputsStyles.label + (field.mandatory ? " " + inputsStyles.labelRequired : "")}>
                  {field.name}
                </legend>
                <div className={inputsStyles.radioButtons}>
                  {(JSON.parse(field.options) as string[]).map((option, index) => (
                    <RadioButton
                      key={index}
                      id={field.id + index}
                      label={option}
                      name={field.id}
                      required={field.mandatory}
                      value={option}
                      hasErrors={!!errors[field.id]}
                      register={register}
                      onFocus={pushGtmFirstClickEvent}
                    />
                  ))}
                </div>
                <FormError errors={errors} name={field.id} type="required">
                  {configuration.requiredFieldError}
                </FormError>
              </fieldset>
            )

          case "DatoCmsCheckboxField":
            return (
              <div className={inputsStyles.formPart + " " + styles.formPart} key={field.id}>
                <Checkbox
                  key={field.id}
                  id={field.id}
                  label={field.name}
                  name={field.id}
                  required={field.mandatory}
                  hasErrors={!!errors[field.id]}
                  register={register}
                  onFocus={pushGtmFirstClickEvent}
                />
                <FormError errors={errors} name={field.id} type="required">
                  {configuration.requiredFieldError}
                </FormError>
              </div>
            )

          case "DatoCmsFileField": {
            const serverErrorMessage = errors[field.id]?.message

            return (
              <div className={inputsStyles.formPart + " " + styles.formPart} key={field.id}>
                <label
                  htmlFor={field.id}
                  className={inputsStyles.label + (field.mandatory ? " " + inputsStyles.labelRequired : "")}
                >
                  {field.name}
                </label>
                <FileInput
                  key={field.id}
                  id={field.id}
                  type="file"
                  name={field.id}
                  placeholder={field.placeholder}
                  required={field.mandatory}
                  onChange={value => onChange(field.id, value)}
                  className={styles.fileField}
                  register={register}
                  onFocus={pushGtmFirstClickEvent}
                  accept=".jpg,.jpeg,.png,.gif,.doc,.docx,.pdf,.xls,.xlsx"
                />
                <FormError errors={errors} name={field.id} type="required">
                  {configuration.requiredFieldError}
                </FormError>
                <FormError errors={errors} name={field.id} type="server">
                  {typeof serverErrorMessage === "string" ? serverErrorMessage : undefined}
                </FormError>
              </div>
            )
          }

          case "DatoCmsSelectField":
            return (
              <div className={inputsStyles.formPart + " " + styles.formPart} key={field.id}>
                <label
                  htmlFor={field.id}
                  className={inputsStyles.label + (field.mandatory ? " " + inputsStyles.labelRequired : "")}
                >
                  {field.name}
                </label>
                <Select
                  key={field.id}
                  id={field.id}
                  name={field.id}
                  autocomplete={getFieldName(field.name)}
                  options={JSON.parse(field.options)}
                  required={field.mandatory}
                  className={styles.select}
                  hasErrors={!!errors[field.id]}
                  register={register}
                  onFocus={pushGtmFirstClickEvent}
                />
                <FormError errors={errors} name={field.id} type="required">
                  {configuration.requiredFieldError}
                </FormError>
              </div>
            )

          case "DatoCmsSimpleText":
            return <RichDatoCmsContent key={field.id} data={[field]} />
        }
      })}

      {formCaptchaKey && (
        <div ref={captchaContainerRef} className={styles.captcha}>
          <ReCAPTCHA sitekey={formCaptchaKey} size="compact" onChange={onChangeCaptcha} />
        </div>
      )}

      <button
        type="submit"
        className={inputsStyles.primaryButton + " " + styles.submitButton}
        disabled={formState === FormState.Submitting || formState === FormState.Success}
      >
        {data.submitButtonLabel}
      </button>
      <GlobalFormError show={formState === FormState.Error} className={styles.globalError}>
        {data.formError}
      </GlobalFormError>
      <output className={styles.successMessage} aria-live="polite" aria-atomic="true">
        {formState === FormState.Success ? data.successMessage : ""}
      </output>
    </form>
  )

  function onChangeCaptcha(value) {
    setCaptchaValue(value)
  }
}

export default CustomForm

export const fragments = graphql`
  fragment CustomFormFields on DatoCmsForm {
    id
    title
    submitButtonLabel
    from
    # receivers {
    #   ...EmailReceiversFields
    # }
    successMessage
    formError
    subsidiaryEmailSubject
    subsidiaryEmailContent {
      value
      blocks
      links {
        id: originalId
        fragmentType
      }
    }
    userEmailSubject
    userEmailContent {
      value
      blocks
      links {
        id: originalId
        fragmentType
      }
    }
    fieldsList {
      __typename
      ... on DatoCmsSingleLineTextField {
        id
        name
        placeholder
        mandatory
        format
      }
      ... on DatoCmsMultiLinesTextField {
        id
        name
        placeholder
        mandatory
      }
      ... on DatoCmsRadioButtonsField {
        id
        name
        mandatory
        options
      }
      ... on DatoCmsCheckboxField {
        id
        name
        mandatory
      }
      ... on DatoCmsFileField {
        id
        name
        mandatory
        placeholder
      }
      ... on DatoCmsSelectField {
        id
        name
        mandatory
        options
      }
      ... on DatoCmsUserEmailField {
        id
        name
        placeholder
        mandatory
      }
      ... on DatoCmsSimpleText {
        ...SimpleTextFields
      }
    }
  }

  fragment EmailReceiversFields on DatoCmsEmailReceiversBlock {
    email
    sendType
  }

  fragment CustomFormsConfigurationFields on DatoCmsFormsConfiguration {
    requiredFieldError
    phoneNumberFieldError
    emailFieldError
    logoSubsidiary {
      alt
      url
    }
  }
`

function toValidationType(format: string) {
  switch (format) {
    case "Email":
      return ValidationType.Email
    case "Phone number":
      return ValidationType.PhoneNumber
    default:
      return ValidationType.None
  }
}

enum FormState {
  Pending,
  Submitting,
  Success,
  Error,
}
